import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';
import { Calculator, DollarSign, TrendingDown, TrendingUp } from 'lucide-react';

interface TaxData {
  gross_income: number;
  taxes: {
    federal: number;
    state: number;
    social_security: number;
    medicare: number;
    total: number;
  };
  net_income: number;
  effective_rates: {
    federal: number;
    state: number;
    total: number;
  };
  take_home_pay: {
    annual: number;
    monthly: number;
    weekly: number;
    daily: number;
    biweekly?: number;
  };
  state: string;
}

const US_STATES = [
  { code: 'AL', name: 'Alabama' },
  { code: 'AK', name: 'Alaska' },
  { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' },
  { code: 'CA', name: 'California' },
  { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' },
  { code: 'DE', name: 'Delaware' },
  { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' },
  { code: 'HI', name: 'Hawaii' },
  { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' },
  { code: 'IN', name: 'Indiana' },
  { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' },
  { code: 'KY', name: 'Kentucky' },
  { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' },
  { code: 'MD', name: 'Maryland' },
  { code: 'MA', name: 'Massachusetts' },
  { code: 'MI', name: 'Michigan' },
  { code: 'MN', name: 'Minnesota' },
  { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' },
  { code: 'MT', name: 'Montana' },
  { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' },
  { code: 'NH', name: 'New Hampshire' },
  { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' },
  { code: 'NY', name: 'New York' },
  { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' },
  { code: 'OH', name: 'Ohio' },
  { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' },
  { code: 'PA', name: 'Pennsylvania' },
  { code: 'RI', name: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina' },
  { code: 'SD', name: 'South Dakota' },
  { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' },
  { code: 'UT', name: 'Utah' },
  { code: 'VT', name: 'Vermont' },
  { code: 'VA', name: 'Virginia' },
  { code: 'WA', name: 'Washington' },
  { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' },
  { code: 'WY', name: 'Wyoming' }
];

interface TaxCalculatorProps {
  initialSalary?: number;
  onTaxDataChange?: (data: TaxData | null) => void;
}

export default function TaxCalculator({ initialSalary = 0, onTaxDataChange }: TaxCalculatorProps) {
  const [enabled, setEnabled] = useState(false);
  const [salary, setSalary] = useState(initialSalary);
  const [state, setState] = useState('CA');

  // Update salary when initialSalary changes
  useEffect(() => {
    if (initialSalary > 0) {
      setSalary(initialSalary);
    }
  }, [initialSalary]);

  const { data: taxData, isLoading, error } = useQuery<TaxData>({
    queryKey: ['tax-calculator', salary, state],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke('api-tax-calculator', {
        body: { salary, state }
      });
      
      if (error) throw error;
      return data;
    },
    enabled: enabled && salary > 0,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  // Notify parent component of tax data changes
  useEffect(() => {
    onTaxDataChange?.(taxData || null);
  }, [taxData, onTaxDataChange]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatPercentage = (rate: number) => {
    return `${rate.toFixed(1)}%`;
  };

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Calculator className="h-5 w-5 text-primary" />
          Income Tax Calculator
        </CardTitle>
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Calculate take-home pay after federal and state taxes
          </p>
          <div className="flex items-center gap-2">
            <Label htmlFor="tax-toggle" className="text-sm">Enable Tax Calculator</Label>
            <Switch
              id="tax-toggle"
              checked={enabled}
              onCheckedChange={setEnabled}
            />
          </div>
        </div>
      </CardHeader>

      {enabled && (
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="salary-input">Annual Salary</Label>
              <Input
                id="salary-input"
                type="number"
                placeholder="Enter annual salary"
                value={salary || ''}
                onChange={(e) => setSalary(Number(e.target.value))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="state-select">State</Label>
              <Select value={state} onValueChange={setState}>
                <SelectTrigger>
                  <SelectValue placeholder="Select state" />
                </SelectTrigger>
                <SelectContent>
                  {US_STATES.map((state) => (
                    <SelectItem key={state.code} value={state.code}>
                      {state.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {isLoading && (
            <div className="space-y-4">
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-20 w-full" />
              <Skeleton className="h-16 w-full" />
            </div>
          )}

          {error && (
            <div className="text-center text-muted-foreground">
              <p>Unable to calculate taxes. Please try again.</p>
            </div>
          )}

          {taxData && (
            <div className="space-y-6">
              {/* Tax Breakdown */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <TrendingDown className="h-4 w-4 text-destructive" />
                      Tax Breakdown
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground">Federal Tax:</span>
                      <span className="font-medium">{formatCurrency(taxData.taxes.federal)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground">State Tax ({taxData.state}):</span>
                      <span className="font-medium">{formatCurrency(taxData.taxes.state)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground">Social Security:</span>
                      <span className="font-medium">{formatCurrency(taxData.taxes.social_security)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground">Medicare:</span>
                      <span className="font-medium">{formatCurrency(taxData.taxes.medicare)}</span>
                    </div>
                    <div className="border-t pt-2">
                      <div className="flex justify-between font-semibold">
                        <span>Total Taxes:</span>
                        <span className="text-destructive">{formatCurrency(taxData.taxes.total)}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <TrendingUp className="h-4 w-4 text-success" />
                      Take-Home Pay
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground">Annual:</span>
                      <span className="font-medium text-success">{formatCurrency(taxData.take_home_pay.annual)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground">Monthly:</span>
                      <span className="font-medium">{formatCurrency(taxData.take_home_pay.monthly)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground">Weekly:</span>
                      <span className="font-medium">{formatCurrency(taxData.take_home_pay.weekly)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground">Daily:</span>
                      <span className="font-medium">{formatCurrency(taxData.take_home_pay.daily)}</span>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Effective Tax Rates */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg">Effective Tax Rates</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    <Badge variant="outline">
                      Federal: {formatPercentage(taxData.effective_rates.federal)}
                    </Badge>
                    <Badge variant="outline">
                      State: {formatPercentage(taxData.effective_rates.state)}
                    </Badge>
                    <Badge variant="destructive">
                      Total: {formatPercentage(taxData.effective_rates.total)}
                    </Badge>
                  </div>
                  <div className="mt-4 p-3 bg-muted rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <DollarSign className="h-4 w-4 text-primary" />
                      <span className="font-medium">Summary</span>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      From your gross salary of {formatCurrency(taxData.gross_income)}, 
                      you'll pay {formatCurrency(taxData.taxes.total)} in taxes ({formatPercentage(taxData.effective_rates.total)}) 
                      and take home {formatCurrency(taxData.net_income)} per year.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
}